"use client";

import { useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Home, RefreshCw, AlertTriangle } from "lucide-react";
import StatusServiceHint from "@/components/ui/StatusServiceHint";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Zaloguj błąd do konsoli (i ewentualnie do zewnętrznego monitoringu).
    console.error(error);
  }, [error]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-[#EEF2FF] flex items-center justify-center px-6 py-16">
      {/* ─── Decorative background ─── */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(59,91,219,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(59,91,219,0.06)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />

        <motion.div
          aria-hidden
          className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-blue-300/30 blur-3xl"
          animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          aria-hidden
          className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-rose-300/30 blur-3xl"
          animate={{ x: [0, -60, 0], y: [0, -40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative max-w-xl w-full text-center">
        {/* Pill */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-rose-200/60 shadow-sm text-xs font-medium text-rose-600 mb-8"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>Wystąpił nieoczekiwany błąd</span>
        </motion.div>

        {/* Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-[#3B5BDB] via-[#5b6ee8] to-[#8b5cf6] shadow-lg shadow-blue-500/25"
        >
          <AlertTriangle className="h-11 w-11 text-white" />
        </motion.div>

        {/* Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900"
        >
          Coś poszło nie tak
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-4 text-base text-slate-500 leading-relaxed max-w-md mx-auto"
        >
          Napotkaliśmy nieoczekiwany problem podczas ładowania tej strony.
          Spróbuj ponownie za chwilę — jeśli błąd się powtarza, sprawdź stan
          naszych usług poniżej.
        </motion.p>

        {error?.digest && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-3 text-xs text-slate-400 font-mono"
          >
            Kod błędu: {error.digest}
          </motion.p>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 flex items-center justify-center gap-3 flex-wrap"
        >
          <button
            onClick={() => reset()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#3B5BDB] to-[#5b6ee8] hover:from-[#2B4BC8] hover:to-[#4a5dd6] text-white text-sm font-semibold shadow-lg shadow-blue-500/25 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Spróbuj ponownie
          </button>
          <Link
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold shadow-sm transition-all"
          >
            <Home className="w-4 h-4" />
            Strona główna
          </Link>
        </motion.div>

        {/* Status usług */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.28 }}
          className="mt-8 flex justify-center"
        >
          <StatusServiceHint />
        </motion.div>

        {/* Footer hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-xs text-slate-400"
        >
          NextSchool · Platforma edukacyjna
        </motion.p>
      </div>
    </div>
  );
}
